/**
 * Lookup slice - reference data (states, countries, equipment, attributes, etc.)
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { LOOKUP, LEASES, MERCHANT_SERVICE_PROVIDERS } from '../../api/endpoints';
import {
  findUnitedStatesId,
  getCachedBusinessCategoriesPayload,
  getCachedCountries,
  getCachedEquipmentTypesPayload,
  getCachedLeasesPayload,
  getCachedManufacturersPayload,
  getCachedMerchantServiceProvidersPayload,
  getCachedStates,
  getCachedUsCountryId,
  setCachedBusinessCategoriesPayload,
  setCachedCountries,
  setCachedEquipmentTypesPayload,
  setCachedLeasesPayload,
  setCachedManufacturersPayload,
  setCachedMerchantServiceProvidersPayload,
  setCachedStates,
  setCachedUsCountryId,
} from '../../utils/lookupSessionCache';

export const getCountries = createAsyncThunk(
  'lookup/getCountries',
  async (arg, { rejectWithValue }) => {
    const force = arg === true || arg?.force === true;
    if (!force) {
      const cached = getCachedCountries();
      if (cached) return cached;
    }
    try {
      const { data } = await api.get('/countries');
      const list = data?.data?.list ?? data?.list ?? data;
      setCachedCountries(list);
      const usId = findUnitedStatesId(list);
      if (usId != null) setCachedUsCountryId(usId);
      return list;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load countries');
      return rejectWithValue(err.response?.data?.message || 'Failed to load countries');
    }
  }
);

export const getUnitedStatesId = createAsyncThunk(
  'lookup/getUnitedStatesId',
  async (arg, { rejectWithValue }) => {
    const force = arg === true || arg?.force === true;
    if (!force) {
      const cachedId = getCachedUsCountryId();
      if (cachedId != null) return cachedId;
      const cachedCountries = getCachedCountries();
      if (cachedCountries) {
        const usId = findUnitedStatesId(cachedCountries);
        if (usId != null) {
          setCachedUsCountryId(usId);
          return usId;
        }
      }
    }
    try {
      const { data } = await api.get('/countries');
      const countries = data?.data?.list ?? data?.list ?? data ?? [];
      setCachedCountries(countries);
      const usId = findUnitedStatesId(countries);
      if (usId != null) setCachedUsCountryId(usId);
      return usId;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load countries');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getStates = createAsyncThunk(
  'lookup/getStates',
  async (arg, { rejectWithValue }) => {
    const countryId = typeof arg === 'object' && arg != null ? arg.countryId ?? arg.id : arg;
    const force = typeof arg === 'object' && arg != null ? arg.force === true : false;
    if (!countryId) return rejectWithValue('No country ID provided');
    if (!force) {
      const cached = getCachedStates(countryId);
      if (cached) return cached;
    }
    try {
      const { data } = await api.get(LOOKUP.COUNTRY_BY_ID(countryId));
      const states = data?.data?.states ?? data?.states ?? data ?? [];
      setCachedStates(countryId, states);
      return states;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load states');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getAttributeTypes = createAsyncThunk(
  'lookup/getAttributeTypes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/attribute-types', { params });
      const list = data?.data?.list ?? data?.list ?? data ?? [];
      if (list?.length && typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem('attributeTypes', JSON.stringify(list));
        } catch (e) {
          console.warn('Could not store attributeTypes in localStorage', e);
        }
      }
      return list;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load attribute types');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getEquipmentTypes = createAsyncThunk(
  'lookup/getEquipmentTypes',
  async (arg, { rejectWithValue }) => {
    const force = arg === true || arg?.force === true;
    if (!force) {
      const cached = getCachedEquipmentTypesPayload();
      if (cached != null) return cached;
    }
    try {
      const { data } = await api.get(LOOKUP.EQUIPMENT_TYPES);
      setCachedEquipmentTypesPayload(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load equipment types');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getEquipmentModels = createAsyncThunk(
  'lookup/getEquipmentModels',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LOOKUP.EQUIPMENT_MODELS, { params });
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load equipment models');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getManufacturers = createAsyncThunk(
  'lookup/getManufacturers',
  async (arg, { rejectWithValue }) => {
    const force = arg === true || arg?.force === true;
    if (!force) {
      const cached = getCachedManufacturersPayload();
      if (cached != null) return cached;
    }
    try {
      const { data } = await api.get(LOOKUP.MANUFACTURERS);
      setCachedManufacturersPayload(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load manufacturers');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getBusinessCategories = createAsyncThunk(
  'lookup/getBusinessCategories',
  async (arg, { rejectWithValue }) => {
    const force = arg === true || arg?.force === true;
    if (!force) {
      const cached = getCachedBusinessCategoriesPayload();
      if (cached != null) return cached;
    }
    try {
      const { data } = await api.get(LOOKUP.BUSINESS_CATEGORIES);
      setCachedBusinessCategoriesPayload(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load business categories');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getLeases = createAsyncThunk(
  'lookup/getLeases',
  async (arg, { rejectWithValue }) => {
    const force = arg === true || arg?.force === true;
    if (!force) {
      const cached = getCachedLeasesPayload();
      if (cached != null) return cached;
    }
    try {
      const { data } = await api.get(LEASES.LIST, { params: { paginate: 0 } });
      setCachedLeasesPayload(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load leases');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getMerchantServiceProviders = createAsyncThunk(
  'lookup/getMerchantServiceProviders',
  async (arg, { rejectWithValue }) => {
    const force = arg === true || arg?.force === true;
    if (!force) {
      const cached = getCachedMerchantServiceProvidersPayload();
      if (cached != null) return cached;
    }
    try {
      const { data } = await api.get(MERCHANT_SERVICE_PROVIDERS.LIST, {
        params: { paginate: 0 },
      });
      setCachedMerchantServiceProvidersPayload(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load merchant service providers');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getBankruptTypes = createAsyncThunk(
  'lookup/getBankruptTypes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LOOKUP.BANKRUPT_TYPES);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load bankrupt types');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

const initialState = {
  countries: [],
  usCountryId: null,
  states: [],
  attributeTypes: [],
  equipmentTypes: null,
  equipmentModels: null,
  manufacturers: null,
  businessCategories: null,
  leases: null,
  merchantServiceProviders: null,
  bankruptTypes: null,
  loading: false,
  error: null,
};

const lookupSlice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    clearLookupError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload ?? [];
      })
      .addCase(getUnitedStatesId.fulfilled, (state, action) => {
        state.loading = false;
        state.usCountryId = action.payload;
      })
      .addCase(getStates.fulfilled, (state, action) => {
        state.loading = false;
        state.states = action.payload ?? [];
      })
      .addCase(getAttributeTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.attributeTypes = action.payload ?? [];
      })
      .addCase(getEquipmentTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.equipmentTypes = action.payload;
      })
      .addCase(getEquipmentModels.fulfilled, (state, action) => {
        state.loading = false;
        state.equipmentModels = action.payload;
      })
      .addCase(getManufacturers.fulfilled, (state, action) => {
        state.loading = false;
        state.manufacturers = action.payload;
      })
      .addCase(getBusinessCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.businessCategories = action.payload;
      })
      .addCase(getLeases.fulfilled, (state, action) => {
        state.loading = false;
        state.leases = action.payload;
      })
      .addCase(getMerchantServiceProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.merchantServiceProviders = action.payload;
      })
      .addCase(getBankruptTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.bankruptTypes = action.payload;
      })
      .addMatcher(
        (a) =>
          [
            getCountries.pending,
            getUnitedStatesId.pending,
            getStates.pending,
            getAttributeTypes.pending,
            getEquipmentTypes.pending,
            getEquipmentModels.pending,
            getManufacturers.pending,
            getBusinessCategories.pending,
            getLeases.pending,
            getMerchantServiceProviders.pending,
            getBankruptTypes.pending,
          ].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getCountries.rejected,
            getUnitedStatesId.rejected,
            getStates.rejected,
            getAttributeTypes.rejected,
            getEquipmentTypes.rejected,
            getEquipmentModels.rejected,
            getManufacturers.rejected,
            getBusinessCategories.rejected,
            getLeases.rejected,
            getMerchantServiceProviders.rejected,
            getBankruptTypes.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      );
  },
});

export const { clearLookupError } = lookupSlice.actions;
export default lookupSlice.reducer;
